import React, { memo, useCallback, useMemo, useState } from 'react';
import { DrugItem } from 'src/api/types';
import cn from 'classnames';
import useCartStore, { cartStoreFallback } from 'src/hooks/useCart';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  generateDrugPageLink,
  generateDrugPageReviewFormLink,
  generateDrugPageReviewsLink
} from 'src/utils/links';
import { FbEvents, pushFbEvent } from 'src/utils/tracking';
import { isNotInStock } from 'src/utils/drugHelper';
import useModalStore from 'src/hooks/useModalStore';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import useDrugListAnalyticStore from 'src/hooks/useDrugListAnalyticStore';
import { useIsMounted } from 'src/hooks/useIsMounted';
import dynamic from 'next/dynamic';
import Skeleton from 'src/shared-components/Loaders/Skeleton';
import useStoreDeep from 'src/hooks/useStore';
import trns from './trns';
import { DrugCardVariant } from './types';
import { ButtonColor, ButtonVariant, ButtonIcon } from '../Button_v3';
import useNotification, { NotificationType } from '../../hooks/useNotification';
import {
  ButtonSecondarySize,
  ButtonSecondaryVariant
} from '../design-system/ButtonSecondary';
import BellIcon from '../../icons/Bell';

const DrugImage = dynamic(() => import('../DrugImage/DrugImage'), {
  loading: () => <Skeleton width={'100%'} height={32} />
});

const Reviews = dynamic(
  () => import('src/page-components/drug/components/ReviewsComponents/Reviews'),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

const NotInStockLabel = dynamic(() => import('./NotInStockLabel'), {
  loading: () => <Skeleton width={'100%'} height={16} />
});

const NavigationLink = dynamic(
  () => import('../NavigationLink/NavigationLink'),
  {
    loading: () => <Skeleton width={'100%'} height={'100%'} />
  }
);

const IconButton = dynamic(
  () => import('../design-system/IconButton/IconButton'),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

const Button = dynamic(() => import('../Button_v3'), {
  loading: () => <Skeleton width={'100%'} height={40} />
});

const ButtonTertiary = dynamic(
  () => import('../design-system/ButtonTertiary'),
  {
    loading: () => <Skeleton width={'100%'} height={40} />
  }
);

const ButtonSecondary = dynamic(
  () => import('../design-system/ButtonSecondary'),
  {
    loading: () => <Skeleton width={'100%'} height={40} />
  }
);

const SpecifyQuantityModal = dynamic(
  () => import('../SpecifyQuantityModal/SpecifyQuantityModal'),
  {
    ssr: false
  }
);

interface Props {
  drug: DrugItem;
  searchValue?: string;
  variant?: DrugCardVariant;
  gtagPrefix?: GTAGPrefix;
  containerClassName?: string;
  // Works with DrugCardVariant.FastSearchCard only. Provide if you want to make a part of title bold
  titleSelectedText?: string;
  onClick?: () => void;
  addToCartGAAction?: string;
  clickOnCardGAAction?: string;
  isPriorityLoading?: boolean;
  addItemToCardHandle?: (drug?: DrugItem) => void;
}

const DrugCard = memo(function DrugCard({
  containerClassName,
  drug,
  searchValue,
  variant = DrugCardVariant.SearchCard,
  gtagPrefix = GTAGPrefix.default,
  titleSelectedText,
  isPriorityLoading,
  onClick = () => null,
  addItemToCardHandle
}: Props) {
  const {
    id,
    translitName,
    name,
    foundInBranchesCount,
    minPrice,
    imageExists,
    analytics
  } = drug;
  const formattedMinPrice = minPrice?.toFixed(2);

  const { isMounted } = useIsMounted();
  const intl = useIntl();
  const updateModalsConfig = useModalStore((state) => state.updateModalsConfig);
  const setNotificationModal = useModalStore(
    (state) => state.setNotificationModal
  );
  const modalsConfig = useModalStore((state) => state.modalsConfig);
  const { trackGtagEvent, trackCustomGtagEvent } = useGTAG();
  const { showNotification } = useNotification();
  const [isHovered, setIsHovered] = useState(false);
  const cartStore = useStoreDeep(useCartStore, (state) => ({
    addItemToCart: state.addItemToCart,
    drugInCart: state.drugInCart
  }));
  const { addItemToCart, drugInCart } = cartStore ?? cartStoreFallback;
  const { sendChangeCartItemsAnalytic } = useAnalytics();

  const quantityModalName = `specify-quantity-${id}-modal`;

  const hideSearchResults = () => {
    updateModalsConfig({ 'search-input-v2-modal': false });
  };

  const handleAddToCart = () => {
    if (!addItemToCardHandle) {
      addItemToCart({
        drug,
        packQuantity: 1,
        unitQuantity: 0,
        gtagPrefix
      });
      setTimeout(() => {
        sendChangeCartItemsAnalytic({
          drug,
          packageQuantityDetails: {
            packQuantity: 1,
            quantityUnitInPack: drug.quantityUnitInPack
          }
        });
      });
    } else {
      addItemToCardHandle(drug);
    }
    setTimeout(() => {
      trackCustomGtagEvent(`${gtagPrefix}_item_cart`, {
        item_name: drug.name,
        search_request: searchValue
      });
    });
  };

  const isDrugInCart = drugInCart(translitName);
  const handleSpecifyQuantityModalOpen = () =>
    updateModalsConfig({ [quantityModalName]: true });

  const isFlatSearchCard =
    variant === DrugCardVariant.FlatFastSearchCard ||
    variant === DrugCardVariant.FastSearchCard ||
    variant === DrugCardVariant.SimpleSearchCard;

  const isMinimalisticCard = variant === DrugCardVariant.MinimalisticCard;

  const photoSizes = useMemo(() => {
    switch (variant) {
      case DrugCardVariant.FlatFastSearchCard:
        return {
          width: 32,
          height: 24
        };
      case DrugCardVariant.FastSearchCard:
        return {
          width: 80,
          height: 50
        };
      case DrugCardVariant.SimpleSearchCard:
        return {
          width: 80,
          height: 50
        };
      default:
        return {
          width: 180,
          height: 150
        };
    }
  }, [variant]);

  const notInStock = useMemo(
    () => isNotInStock(minPrice, foundInBranchesCount),
    [minPrice, foundInBranchesCount]
  );
  const setDrugListAnalytics = useDrugListAnalyticStore(
    (state) => state.setDrugListAnalytics
  );

  const drugCardHref = generateDrugPageLink({ translitName });

  const drugCardReviewsHref = generateDrugPageReviewsLink({
    translitName
  });
  const drugCardReviewFormHref = generateDrugPageReviewFormLink({
    translitName
  });

  const imageVariantStyling = useMemo(() => {
    switch (variant) {
      case DrugCardVariant.MinimalisticCard:
        return 'mb-[21px]';
      case DrugCardVariant.FastSearchCard:
        return ' mr-4';
      case DrugCardVariant.SimpleSearchCard:
        return ' mr-4';
      case DrugCardVariant.FlatFastSearchCard:
        return 'overflow-visible';
      default:
        // DrugCardVariant.SearchCard as default
        return 'm:mb-[31px] mb-3.5';
    }
  }, [variant]);

  const handleCardClick = () => {
    onClick();
    setTimeout(() => {
      pushFbEvent(FbEvents.ViewContent);
      setDrugListAnalytics(analytics);
      trackGtagEvent(`${gtagPrefix}_item`, drug.name);
    }, 500);
  };

  const callNotificationModal = useCallback(
    (value) => {
      setNotificationModal({
        id,
        translitName,
        name,
        gtagPrefix
      });
      updateModalsConfig({ [`notification-modal-${translitName}`]: value });
      setTimeout(() => {
        trackGtagEvent(`${gtagPrefix}_item_inform`, drug.name);
      }, 250);
    },
    [translitName]
  );

  const isNotificationModalVisible = useMemo(
    () => modalsConfig[quantityModalName],
    [modalsConfig[quantityModalName]]
  );

  const cardControls = useMemo(() => {
    switch (variant) {
      case DrugCardVariant.MinimalisticCard:
        return (
          <div className="flex flex-col h-3/6 justify-between">
            <NavigationLink
              passClassName={cn(
                'text-left line-clamp-3',
                isHovered && !notInStock && 'text-endeavour',
                notInStock ? 'text-silver' : 'text-mako mb-[42px]'
              )}
              href={drugCardHref}
              onClick={handleCardClick}
            >
              {name}
            </NavigationLink>
            {/* Hide review form link from crawling bots */}
            <div className="flex flex-col items-start justify-end">
              {isMounted && (
                <NavigationLink
                  href={
                    drug?.rateCount === 0
                      ? drugCardReviewFormHref
                      : drugCardReviewsHref
                  }
                  onClick={handleCardClick}
                >
                  <Reviews
                    averageRate={drug?.averageRate}
                    rateCount={drug?.rateCount}
                    isStars={drug?.rateCount !== 0}
                  />
                </NavigationLink>
              )}
              {notInStock ? (
                <NotInStockLabel
                  setIsNotificationModalVisible={callNotificationModal}
                />
              ) : (
                <div className="flex justify-between">
                  <p className="text-shuttleGray fs-caption-reg">
                    <FormattedMessage
                      {...trns.priceLabelFrom}
                      values={{
                        price: (
                          <span className="fs-body-lg-reg text-vermilion uppercase">
                            {formattedMinPrice} ₴
                          </span>
                        )
                      }}
                    />
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      case DrugCardVariant.FastSearchCard:
        return (
          <div className="flex flex-row justify-between w-full h-auto">
            <NavigationLink
              href={drugCardHref}
              passClassName={cn(
                'flex flex-row w-1/2 text-left text-black line-clamp-2 fs-caption-reg',
                isHovered && !notInStock && 'fs-body-bold'
              )}
              onClick={() => {
                onClick();
                setDrugListAnalytics(analytics);
                hideSearchResults();
                trackCustomGtagEvent(`${gtagPrefix}_item`, {
                  item_name: drug.name,
                  search_request: searchValue
                });
              }}
            >
              <div className="flex flex-row justify-start mr-2.5">
                <div>
                  <NavigationLink
                    href={drugCardHref}
                    passClassName={cn(
                      'cursor-pointer flex items-center overflow-hidden h-auto',
                      imageVariantStyling
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center relative',
                        notInStock && 'grayscale'
                      )}
                      style={{ ...photoSizes }}
                    >
                      <DrugImage
                        layout={'responsive'}
                        objectFit="contain"
                        drugId={id}
                        size="M"
                        drugName={name}
                        quality={25}
                        fill
                        isPriorityLoading={isPriorityLoading}
                        imageExists={imageExists}
                      />
                    </div>
                  </NavigationLink>
                </div>

                <span className="fs-body-reg">{titleSelectedText}</span>
                <span
                  className={cn(
                    'fs-body-reg line-clamp-2',
                    isHovered && 'text-bahamaBlue'
                  )}
                >
                  {name.replace(titleSelectedText, '')}
                </span>
              </div>
            </NavigationLink>

            {notInStock ? (
              <div className="flex justify-between w-1/2">
                <p className="lowercase text-doveGray fs-body-bold">
                  <FormattedMessage {...trns.notInStock} />
                </p>
                <ButtonTertiary
                  onClick={() => callNotificationModal(true)}
                  buttonLabel={<FormattedMessage {...trns.notify} />}
                  icon={{
                    alt: 'bell',
                    component: BellIcon
                  }}
                />
              </div>
            ) : (
              <div className="flex justify-between w-1/2">
                <p className="fs-body-bold">
                  <span className="uppercase">{formattedMinPrice} ₴</span>
                </p>
                {isDrugInCart ? (
                  <Button
                    onClick={() =>
                      setTimeout(() => {
                        trackGtagEvent(`${gtagPrefix}_item_added`, drug.name);
                      }, 250)
                    }
                    color={ButtonColor.vermilion}
                    variant={ButtonVariant.regular}
                    buttonLabel={
                      <p className="text-white fs-body-bold ">
                        <FormattedMessage {...trns.alreadyInList} />
                      </p>
                    }
                    icon={{ variant: ButtonIcon.checkIcon, alt: 'checkIcon' }}
                  />
                ) : (
                  <Button
                    onClick={handleAddToCart}
                    color={ButtonColor.gray}
                    variant={ButtonVariant.regular}
                    buttonLabel={
                      <p className="text-tundora fs-body-bold ">
                        <FormattedMessage {...trns.addToList} />
                      </p>
                    }
                    icon={{
                      variant: ButtonIcon.listIcon,
                      alt: 'listIcon',
                      size: { width: 24, height: 24 },
                      priority: true
                    }}
                  />
                )}
              </div>
            )}
          </div>
        );
      case DrugCardVariant.FlatFastSearchCard:
        return (
          <div className="flex flex-row justify-between items-center w-full">
            <NavigationLink
              href={drugCardHref}
              passClassName={cn(
                'flex flex-row  text-left text-black line-clamp-2 fs-caption-reg',
                isHovered && !notInStock && 'fs-body-bold'
              )}
              onClick={hideSearchResults}
            >
              <div className="flex flex-row justify-start mr-2.5">
                <div>
                  <NavigationLink
                    href={drugCardHref}
                    passClassName={cn(
                      'cursor-pointer flex items-center overflow-hidden h-auto',
                      imageVariantStyling
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center relative',
                        notInStock && 'grayscale'
                      )}
                      style={{ ...photoSizes }}
                    >
                      <DrugImage
                        layout={isFlatSearchCard ? 'fixed' : 'responsive'}
                        objectFit="contain"
                        drugId={id}
                        size="L"
                        drugName={name}
                        quality={25}
                        fill
                        isPriorityLoading={isPriorityLoading}
                        imageExists={imageExists}
                      />
                    </div>
                  </NavigationLink>
                </div>
                <div className="flex flex-col ml-4">
                  <span className="fs-body-reg">{titleSelectedText}</span>
                  <div className="mb-1 text-black line-clamp-2">
                    <span className="fs-body-reg">
                      {name.replace(titleSelectedText, '')}
                    </span>
                  </div>
                  {notInStock ? (
                    <p className="lowercase text-doveGray fs-body-bold">
                      <FormattedMessage {...trns.notInStock} />
                    </p>
                  ) : (
                    <p className="fs-body-bold">
                      <span className="uppercase">{formattedMinPrice} ₴</span>
                    </p>
                  )}
                </div>
              </div>
            </NavigationLink>

            {notInStock ? (
              <div className="flex justify-between">
                <IconButton
                  onClick={() => callNotificationModal(true)}
                  icon={{
                    alt: 'bell',
                    component: BellIcon
                  }}
                />
              </div>
            ) : (
              <div>
                {isDrugInCart ? (
                  <Button
                    color={ButtonColor.vermilion}
                    variant={ButtonVariant.minimalistic}
                    buttonLabel={
                      <p className="text-white fs-body-bold ">
                        <FormattedMessage {...trns.alreadyInList} />
                      </p>
                    }
                    icon={{ variant: ButtonIcon.checkIcon, alt: 'checkIcon' }}
                  />
                ) : (
                  <Button
                    onClick={handleAddToCart}
                    color={ButtonColor.gray}
                    variant={ButtonVariant.minimalistic}
                    buttonLabel={
                      <p className="text-tundora fs-body-bold ">
                        <FormattedMessage {...trns.addToList} />
                      </p>
                    }
                    icon={{
                      variant: ButtonIcon.listIcon,
                      alt: 'listIcon',
                      size: { width: 20, height: 20 },
                      priority: true
                    }}
                  />
                )}
              </div>
            )}
          </div>
        );
      case DrugCardVariant.SimpleSearchCard:
        return (
          <div className="flex flex-col sm:flex-row justify-between w-full h-auto">
            <div className="flex flex-row justify-start items-center mr-2.5">
              <div>
                <div
                  className={cn(
                    'flex items-center relative',
                    notInStock && 'grayscale'
                  )}
                  style={{ ...photoSizes }}
                >
                  <DrugImage
                    layout={'responsive'}
                    objectFit="contain"
                    drugId={id}
                    size="M"
                    drugName={name}
                    quality={25}
                    fill
                    isPriorityLoading={isPriorityLoading}
                    imageExists={imageExists}
                  />
                </div>
              </div>
              <span className="fs-body-reg">{titleSelectedText}</span>
              <span className="fs-body-reg line-clamp-2 min-w-[140px]">
                {name.replace(titleSelectedText, '')}
              </span>
              {formattedMinPrice && (
                <p className="hidden sm:block fs-body-bold ml-4">
                  <span className="uppercase">{formattedMinPrice} ₴</span>
                </p>
              )}
              {notInStock && (
                <p className="hidden sm:block lowercase text-shuttleGray fs-caption-reg ml-4">
                  <FormattedMessage {...trns.notInStock} />
                </p>
              )}
            </div>

            {notInStock ? (
              <div className="flex flex-col items-center">
                <p className="sm:hidden my-2 block lowercase text-shuttleGray fs-caption-reg ml-4">
                  <FormattedMessage {...trns.notInStock} />
                </p>
                <NotInStockLabel
                  isLabelVisible={false}
                  wrapperClassName="flex justify-between flex-col sm:flex-row items-center"
                  setIsNotificationModalVisible={callNotificationModal}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center">
                {formattedMinPrice && (
                  <p className="block my-2 sm:hidden fs-body-bold ml-4">
                    <span className="uppercase">{formattedMinPrice} ₴</span>
                  </p>
                )}
                <Button
                  onClick={(event: React.FormEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    event.preventDefault();
                    handleAddToCart();
                  }}
                  className="w-[122px]"
                  color={ButtonColor.blue}
                  variant={ButtonVariant.regular}
                  buttonLabel={
                    <p className="text-white fs-body-bold ">
                      <FormattedMessage {...trns.addDrugToCart} />
                    </p>
                  }
                />
              </div>
            )}
          </div>
        );
      default:
        // DrugCardVariant.SearchCard as default
        return (
          <>
            <div className="flex flex-col justify-between h-full">
              <div className="h-1/3 flex flex-col items-start justify-start">
                <NavigationLink
                  href={drugCardHref}
                  passClassName={cn(
                    'text-left line-clamp-3',
                    isHovered && !notInStock && 'text-endeavour',
                    notInStock ? 'text-silver' : 'text-mako'
                  )}
                  onClick={handleCardClick}
                >
                  {name}
                </NavigationLink>
              </div>
              {/* Hide review form link from crawling bots */}
              {isMounted && (
                <NavigationLink
                  href={
                    drug?.rateCount === 0
                      ? drugCardReviewFormHref
                      : drugCardReviewsHref
                  }
                  onClick={handleCardClick}
                >
                  <Reviews
                    averageRate={drug?.averageRate}
                    rateCount={drug?.rateCount}
                    isStars={drug?.rateCount !== 0}
                  />
                </NavigationLink>
              )}
              {notInStock ? (
                <div className="pt-6 m:pt-[34px] text-center flex items-start justify-center h-2/3">
                  <NotInStockLabel
                    setIsNotificationModalVisible={callNotificationModal}
                  />
                </div>
              ) : (
                <div className="pt-6 m:pt-[34px] flex flex-col w-full h-2/3">
                  <p className="text-shuttleGray fs-caption-reg text-center">
                    <FormattedMessage
                      {...trns.priceLabelFrom}
                      values={{
                        price: (
                          <span className="fs-body-lg-reg text-vermilion uppercase">
                            {formattedMinPrice} ₴
                          </span>
                        )
                      }}
                    />
                  </p>
                  <div className="pt-[19px] flex items-center justify-center">
                    <ButtonSecondary
                      onClick={() => {
                        handleSpecifyQuantityModalOpen();
                        setTimeout(() => {
                          trackGtagEvent(
                            `${gtagPrefix}_item_pharmacy_choose`,
                            drug.name
                          );
                        }, 250);
                      }}
                      className="w-full"
                      size={ButtonSecondarySize.xl}
                      data-cy="select_the_shop_button_drug_card"
                      label={
                        <p className="text-white opacity-90 fs-body-bold ">
                          <FormattedMessage {...trns.selectTheShop} />
                        </p>
                      }
                      variant={ButtonSecondaryVariant.regular}
                    />
                  </div>

                  <p className="text-mako fs-caption-reg pt-[34px] text-center">
                    <FormattedMessage
                      {...trns.countLabel}
                      values={{
                        count: (
                          <span className="fs-caption-reg">
                            {foundInBranchesCount}
                          </span>
                        )
                      }}
                    />
                  </p>
                </div>
              )}
            </div>
            {isMounted && isNotificationModalVisible && (
              <SpecifyQuantityModal
                drug={drug}
                quantityModalName={quantityModalName}
              />
            )}
          </>
        );
    }
  }, [
    variant,
    isHovered,
    isDrugInCart,
    name,
    notInStock,
    drugCardHref,
    foundInBranchesCount,
    formattedMinPrice
  ]);

  const variantStyling = useMemo(() => {
    switch (variant) {
      case DrugCardVariant.MinimalisticCard:
        return 'h-auto pt-1.5 m:pt-6 pb-4 m:pb-9';
      case DrugCardVariant.FastSearchCard:
        return 'min-h-20.5 py-3';
      case DrugCardVariant.FlatFastSearchCard:
        return 'min-h-20.5 py-3';
      case DrugCardVariant.SimpleSearchCard:
        return 'pt-[5px] sm:pt-[17px] pb-[13px] m:pb-[17px] min-h-full';
      default:
        // DrugCardVariant.SearchCard as default
        return 'pt-1 m:pt-6 pb-[13px] m:pb-[17px] min-h-full';
    }
  }, [variant]);

  const triggerHovered = () => {
    setIsHovered(!isHovered);
  };

  const handleAddItemToCart = () => {
    showNotification({
      message: intl.formatMessage(trns.itemAddToCart),
      type: NotificationType.Success
    });
    addItemToCart({
      drug,
      packQuantity: 1,
      unitQuantity: 0,
      gtagPrefix
    });
    setTimeout(() => {
      onClick();
      sendChangeCartItemsAnalytic({
        drug,
        packageQuantityDetails: { packQuantity: 1 }
      });
      trackGtagEvent(`${gtagPrefix}_item_cart`, drug.name);
      setDrugListAnalytics(analytics);
      if (isDrugInCart) {
        trackGtagEvent(`${gtagPrefix}_item_added`, drug.name);
      }
    });
  };

  return (
    <div
      data-cy={`drug_card_${drug.translitName}`}
      onMouseEnter={triggerHovered}
      onMouseLeave={triggerHovered}
      className={cn(
        'flex flex-col relative w-full px-2.5 m:px-4 border-gallery bg-white overflow-hidden',
        isFlatSearchCard ? 'border-b last:border-none' : 'border',
        variantStyling,
        isHovered && 'shadow-xl compendium-drug-card-hover',
        containerClassName
      )}
    >
      <div className="flex items-center justify-center absolute top-[16px] right-[16px] z-10">
        {!notInStock && !isFlatSearchCard && (
          <Button
            className={cn('w-[40px]', isDrugInCart && 'cursor-default')}
            color={isDrugInCart ? ButtonColor.vermilion : ButtonColor.gray}
            iconClass="justify-center"
            variant={ButtonVariant.minimalistic}
            onClick={handleAddItemToCart}
            icon={{
              variant: isDrugInCart
                ? ButtonIcon.checkIcon
                : ButtonIcon.listIcon,
              alt: isDrugInCart ? 'checkIcon' : 'listIcon',
              size: { width: 24, height: 24 },
              priority: !isMinimalisticCard
            }}
          />
        )}
      </div>
      <NavigationLink
        href={drugCardHref}
        passClassName={cn(
          isFlatSearchCard && 'hidden',
          'cursor-pointer flex flex-col w-full max-h-[150px] min-h-[150px] items-center justify-center overflow-hidden',
          imageVariantStyling
        )}
        onClick={() => {
          onClick();
          setTimeout(() => {
            trackGtagEvent(`${gtagPrefix}_item`, drug.name);
            setDrugListAnalytics(analytics);
          }, 250);
        }}
      >
        <div
          className={cn(
            'flex w-full h-full items-center justify-center relative',
            notInStock && 'grayscale'
          )}
          style={{ ...photoSizes }}
        >
          <DrugImage
            layout={isFlatSearchCard ? 'fixed' : 'responsive'}
            objectFit="contain"
            drugId={id}
            size="M"
            drugName={name}
            quality={25}
            fill
            isPriorityLoading={isPriorityLoading}
            imageExists={imageExists}
          />
        </div>
      </NavigationLink>
      {cardControls}
    </div>
  );
});

export default DrugCard;
